import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { ResourceDWR } from "../../../../dto/ResourceDWR";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { getISBNList } from "@tlc/ls2pac/app/utils/PACStandardNumberUtil";

interface NovelistContentProps {
  resource: ResourceDWR;
}

export const NovelistContent = ({ resource }: NovelistContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(
    typeof window.novSelect !== "undefined"
  );

  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();
  const noveListSettings = locationConfiguration?.pacSettings?.noveListSettings;
  const enabled = !!noveListSettings?.useNoveList;

  const loadScript = (): Promise<void> => {
    if (scriptLoaded || typeof window.novSelect !== "undefined") {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "//imageserver.ebscohost.com/novelistselect/ns2init.js";
      script.async = true;

      script.onload = () => {
        setScriptLoaded(true);
        resolve();
      };

      script.onerror = () => {
        console.error("Failed to load Novelist script");
        reject(new Error("Novelist script failed to load"));
      };

      document.head.appendChild(script);
    });
  };

  const loadContentForISBN = async () => {
    if (!noveListSettings?.useNoveList) {
      throw new Error("NoveList is not enabled or configured.");
    }

    const target = containerRef.current;
    if (!target) {
      throw new Error("Novelist target not found");
    }

    const isbns = getISBNList(resource.standardNumbers);
    const isbn = isbns.find((i) => i.length === 13);
    if (!isbn) {
      target.removeAttribute("data-novelist-novelistselect");
      setError(true);
      setLoading(false);
      return;
    }

    target.setAttribute("data-novelist-novelistselect", isbn);

    try {
      await loadScript();

      window.novSelect?.loadContentForISBN(
        isbn,
        noveListSettings.noveListAccount,
        noveListSettings.noveListPassword,
        (response) => {
          const hasContent =
            Array.isArray(response) &&
            response.length > 0 &&
            (response.length > 1 || response[0].content?.trim());

          setError(!hasContent);
        }
      );
    } catch (e) {
      console.error("Error loading NoveList content", e);
      setError(true);
    } finally {
      // Give some time for Novelist to finish rendering
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (!enabled || !resource) return;

    loadContentForISBN();
  }, [enabled, resource?.id]);

  if (!enabled) return null;

  return (
    <Accordion expanded={!loading && !error}>
      <AccordionSummary>
        {!error && loading && <Skeleton width={200} />}
        {error && <p>{translate("noNoveListContent")}</p>}
      </AccordionSummary>
      <AccordionDetails ref={containerRef}></AccordionDetails>
    </Accordion>
  );
};
